import * as React from "react"
import { ChevronRight } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * The iOS-style grouped list — the phone's way of laying out settings and
 * action menus. One rounded surface per group, rows separated by a hairline
 * that starts where the TEXT starts (`--row-inset`), a label above the group
 * and an optional footnote below it. The look lives in globals.css
 * (`.list-group`, `.list-row`) so a hand-built row can join a group too.
 *
 * ⚠️ Rows answer a press with a background tint, NEVER a scale. A list row is
 * a container, and shrinking a container on press moves the target out from
 * under the finger before release — the click silently dies (the rule in
 * PROJECT_OVERVIEW → Conventions, learned in the events journal).
 */

function ListGroup({
  label,
  footer,
  className,
  children,
}: {
  label?: React.ReactNode
  footer?: React.ReactNode
  className?: string
  children: React.ReactNode
}) {
  return (
    <section className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <h3 className="px-4 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </h3>
      )}
      <div className="list-group">{children}</div>
      {footer && <p className="px-4 text-xs leading-relaxed text-muted-foreground">{footer}</p>}
    </section>
  )
}

type ListRowProps = {
  icon?: React.ElementType
  /** Tile colour for the icon, e.g. `bg-emerald-500 text-white` (Settings). */
  iconClassName?: string
  label: React.ReactNode
  /** Second line, muted. */
  detail?: React.ReactNode
  /** Optional label layout override for a specific list (e.g. two-line Settings titles). */
  labelClassName?: string
  /** Right-aligned value before the chevron („ჩართულია", a count, a name). */
  value?: React.ReactNode
  chevron?: boolean
  destructive?: boolean
  disabled?: boolean
  /** The row whose content is showing beside the list (desktop master–detail,
   *  e.g. settings). Tinted, its icon filled — the way the sidebar marks „here". */
  selected?: boolean
  onClick?: () => void
}

function ListRow({
  icon: Icon,
  iconClassName,
  label,
  detail,
  labelClassName,
  value,
  chevron = true,
  destructive,
  disabled,
  selected,
  onClick,
}: ListRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "list-row flex min-h-[3.25rem] w-full items-center gap-3 px-3 py-2 text-left transition-colors duration-150 hover:bg-muted/40 active:bg-muted/70 disabled:opacity-50 [-webkit-tap-highlight-color:transparent]",
        selected && "bg-primary/10 hover:bg-primary/10 dark:bg-primary/15"
      )}
    >
      {Icon && (
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-[10px]",
            destructive
              ? "bg-red-500/10 text-red-600 dark:text-red-400"
              : selected
                ? "bg-primary text-primary-foreground"
                : iconClassName ?? "bg-primary/10 text-primary"
          )}
        >
          <Icon className="size-[18px]" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-[15px] font-semibold",
            labelClassName,
            destructive ? "text-red-600 dark:text-red-400" : "text-foreground"
          )}
        >
          {label}
        </span>
        {detail && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{detail}</span>}
      </span>
      {value != null && value !== "" && (
        <span className="max-w-[40%] shrink-0 truncate text-[13px] text-muted-foreground">{value}</span>
      )}
      {chevron && !destructive && <ChevronRight className="size-4 shrink-0 text-muted-foreground/60" />}
    </button>
  )
}

export { ListGroup, ListRow }
